import type { Metadata } from "@/lib/head";

import ToolIntro from "@/pages/tools/_ui/tool-intro";
import LinkTabs from "@/components/link-tabs";
import Pill from "@buildpy/ui/pill";
import type { BlankContractForm } from "@buildpy/core/modules/contract/print-model";
import type { ToolsContractFormView } from "@buildpy/core/views/g4c/tools-contract-form";
import A4Fit from "@/modules/print/a4-fit";
import ContractDocument from "@/modules/print/contract-document";
import PrintButton from "@/modules/print/print-button";

interface Props {
  data: ToolsContractFormView;
}

const BASE = "/tools/forms/contract";
const MOLIT_HREF = `${BASE}?form=molit`;

export const METADATA: Record<BlankContractForm, Metadata> = {
  FTC_10079: {
    title: "인테리어 계약서 양식 (공정위 표준) — 무료 인쇄",
    description:
      "공정거래위원회 실내건축·창호 공사 표준계약서(표준약관 제10079호) 빈 양식. 조항 원문 그대로, 로그인 없이 바로 인쇄합니다. 국토부 민간건설공사 표준도급계약서도 함께.",
  },
  MOLIT: {
    title: "인테리어 계약서 양식 (국토부 민간건설공사 표준도급) — 무료 인쇄",
    description:
      "국토교통부 민간건설공사 표준도급계약서(고시 제2026-34호) 빈 양식. 조항 원문 그대로, 로그인 없이 바로 인쇄합니다. 공정위 실내건축·창호 표준계약서도 함께.",
  },
};

// 탭(?form=)마다 문서 제목·설명이 따라간다.
export const generateMetadata = (form: BlankContractForm): Metadata => METADATA[form];

// 빈 계약서 — 조항은 표준 원문 그대로, 당사자·금액·기간은 손으로 채운다.
const ContractFormPage = ({ data }: Props) => {
  const { form, model, badge } = data;

  return (
    <div className="flex flex-col gap-4">
      <ToolIntro
        actions={<PrintButton label="양식 인쇄" />}
        description={
          <>
            {badge && <Pill className="mr-1.5 align-middle">{badge}</Pill>}
            {model.attribution ?? "표준 양식"} — 조항 원문을 그대로 옮긴 빈 양식입니다. 빈칸을 손으로 채워 쓰세요.
          </>
        }
        title="인테리어 계약서 양식"
      />
      <LinkTabs
        aria-label="계약서 종류"
        activeHref={form === "MOLIT" ? MOLIT_HREF : BASE}
        className="print-hide"
        items={[
          { label: "공정위 실내건축·창호", href: BASE },
          { label: "국토부 민간건설공사", href: MOLIT_HREF },
        ]}
      />
      <div className="print-root">
        <A4Fit>
          <ContractDocument model={model} />
        </A4Fit>
      </div>
    </div>
  );
};

export default ContractFormPage;
